import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { If, List, ListDataRefresher, Cell, NewLine, SimpleBlock, useShowContextRequired } from 'ui-builder';
import {
  PROJECT_CLOSING_DATE_FIELD_UPDATED,
  PROJECT_COMMISSION_FIELD_UPDATED,
  PROJECT_DETAILS_FIELDS_UPDATED,
  ProjectDetailsField,
  ProjectDetailsFieldPermission,
  ProjectDetailsFieldType,
  ProjectType,
  TeamPermission,
  useProjectDetailsFieldsService, useSetupAreaIdContext
} from 'nekst-api';
import TextField from '../../../shared/uibuilder/show/field/TextField';
import ProjectDetailsFieldTypeField from './ProjectDetailsFieldTypeField';
import UpdateProjectDetailsFieldButton from '../CreateUpdate/UpdateProjectDetailsFieldButton';
import ProjectDetailsFieldVisibilityField from './ProjectDetailsFieldVisibilityField';
import DeleteProjectDetailsFieldButton from './DeleteProjectDetailsFieldButton';

import Checkbox from '../../../shared/uibuilder/form/input/Checkbox';
import { withAccessCheck } from 'authorization-scope';
import DetailsFieldsTabs from '../shared/DetailsFieldsTabs';
import SetProjectDatesFieldsForm from './SetProjectDatesFieldsForm';
import { GridLayoutWithSort } from 'features/nekst-widgets';

import styles from './ProjectDetailsFieldsList.module.scss';
import { useSetupAreaLabelsHelper } from '../../markets/setupAreaLabelsHelper';
import CreateProjectDetailsFieldButton from '../CreateUpdate/CreateProjectDetailsFieldButton';
import IsCommissionField from "./IsCommissionField";
import { IsEndDateField, IsStartDateField } from "./StartEndDateFields";
import IsClosingDateField from "./IsClosingDateField";

function ShowHiddenFieldsToggle(
  props: {
    value: boolean,
    setValue: (newValue: boolean) => void
  }
) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <Checkbox
        source="showHiddenFields"
        value={props.value}
        label="Show Hidden Detail Boxes"
        onChangeCallback={() => props.setValue(!props.value)}
      />
    </div>
  );
}

function NameField() {
  const showContext = useShowContextRequired<ProjectDetailsField>();

  const name = showContext.data.name;

  if (showContext.data.originalName) {
    return (
      <>
        {name}
        {' '}
        <span className={styles.originalName}>({showContext.data.originalName})</span>
      </>
    )
  } else {
    return name;
  }
}

export function ProjectDetailsFieldsList(props: {
  projectType: ProjectType;
  withParentField?: boolean;
}) {
  const [showHiddenDetailBoxes, setShowHiddenDetailBoxes] = useState(true);

  const projectDetailsFieldsService = useProjectDetailsFieldsService();

  const projectType = props.projectType;

  const service = useProjectDetailsFieldsService();

  const idContext = useSetupAreaIdContext();

  const labelHelper = useSetupAreaLabelsHelper();

  if (projectType) {
    const fields = (
      <GridLayoutWithSort<ProjectDetailsField>
        orderingConfiguration={{
          isEnabled: true,
          updateOrderFunc: async (ids: number[]) => {
            await service.setOrder(projectType, ids);
          },
          isItemDraggable: (item: ProjectDetailsField) => {
            return true;
          },
          getOrderGroupFunc: (item) => {
            return item.fieldType !== ProjectDetailsFieldType.LONG_TEXT ? 'standard' : 'long_text';
          }
        }}
        noDataMessage="Detail Boxes are not created yet"
      >
        <Cell label="Detail Boxes">
          <NameField />
          <NewLine />
          <TextField source="alias" className={styles.aliasField} />
        </Cell>

        <Cell label={labelHelper.getFieldTypeLabel()}>
          <ProjectDetailsFieldTypeField
            source="fieldType"
            label={labelHelper.getFieldTypeLabel()}
          />
          <IsClosingDateField />
          <IsCommissionField />
          <IsStartDateField />
          <IsEndDateField />
        </Cell>

        {props.withParentField ?
          <TextField source="_parentField.name" label="Parent Box" noDataText="-" /> : null}
        <Cell label="Actions">
          <UpdateProjectDetailsFieldButton projectType={projectType} />
          <If<ProjectDetailsField>
            conditionFunc={(data) => !data.permissions.includes(ProjectDetailsFieldPermission.DELETE)}>
            <ProjectDetailsFieldVisibilityField source="isHidden" />
          </If>
          <If<ProjectDetailsField>
            conditionFunc={(data) => data.permissions.includes(ProjectDetailsFieldPermission.DELETE)}>
            <DeleteProjectDetailsFieldButton projectType={projectType} />
          </If>
        </Cell>
      </GridLayoutWithSort>
    );

    return (
      <>
        {(!idContext?.setupAreaId || idContext.setupAreaId === 'internal') &&
          <DetailsFieldsTabs projectType={projectType} currentSetId={0} />}
        <List
          getDataFunc={async (): Promise<ProjectDetailsField[]> => {
            const result = await projectDetailsFieldsService.getFields(
              projectType,
              true
            );

            return result.filter((item) => {
              if (showHiddenDetailBoxes) {
                return true;
              } else {
                return !item.isHidden;
              }
            });
          }}
          dependencies={[
            projectType,
            showHiddenDetailBoxes
          ]}
        >
          <SetProjectDatesFieldsForm projectType={projectType} />
          <SimpleBlock className={styles.buttonsRow}>
            <ShowHiddenFieldsToggle
              value={showHiddenDetailBoxes}
              setValue={setShowHiddenDetailBoxes}
            />
            <CreateProjectDetailsFieldButton projectType={props.projectType} />
          </SimpleBlock>
          {fields}
          <ListDataRefresher
            eventName={[
              PROJECT_DETAILS_FIELDS_UPDATED,
              PROJECT_CLOSING_DATE_FIELD_UPDATED,
              PROJECT_COMMISSION_FIELD_UPDATED
            ]}
            debounceMs={1000}
          />
        </List>
      </>
    );
  } else {
    return null;
  }
}

export function ProjectDetailsFieldsListPage() {
  const { projectType: projectTypeParam } = useParams<{ projectType: string }>();
  const projectType = projectTypeParam?.toUpperCase() as ProjectType;

  return (
    <ProjectDetailsFieldsList
      projectType={projectType}
    />
  );
}

export default withAccessCheck(ProjectDetailsFieldsListPage, TeamPermission.MANAGE_PROJECT_DETAILS_FIELDS);
